import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import AboutImg from "../assets/images/about.png";
import Banner from "../Components/Banner";
import ServiceCard from "../Components/ServiceCard";
import { aboutUsPageData } from "../Constants/data.jsx";
import { ArrowLeft, ArrowRight } from "react-bootstrap-icons";
import { Helmet } from "react-helmet-async";
import { useServices } from "../hooks/useServices";

const AboutUs = () => {
  const navigate = useNavigate();

  // Use the services hook - API ONLY
  const { services } = useServices();

  // =============== Temple Services state (same behaviour as Home) ===============
  const [isMobile, setIsMobile] = useState(false);
  const [serviceCurrentIndex, setServiceCurrentIndex] = useState(0);

  const servicesRowRef = useRef(null);
  const [serviceTouchStart, setServiceTouchStart] = useState(null);
  const [serviceTouchEnd, setServiceTouchEnd] = useState(null);

  // detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // UPDATE: Use slug instead of ID
  const handleServiceClick = (serviceSlug) => {
    navigate(`/service/${serviceSlug}`);
  };

  // Update the handleServiceNext and handleServicePrev functions:
  const handleServiceNext = () => {
    if (services.length === 0) return;

    if (isMobile) {
      setServiceCurrentIndex((i) => (i + 1) % services.length);
    } else if (services.length > 3) {
      // For desktop carousel with more than 3 cards
      setServiceCurrentIndex((i) => (i + 1) % services.length);
    }
  };

  const handleServicePrev = () => {
    if (services.length === 0) return;

    if (isMobile) {
      setServiceCurrentIndex(
        (i) => (i - 1 + services.length) % services.length
      );
    } else if (services.length > 3) {
      // For desktop carousel with more than 3 cards
      setServiceCurrentIndex(
        (i) => (i - 1 + services.length) % services.length
      );
    }
  };

  // Swipe controls for Temple Services
  const onServiceTouchStart = (e) => {
    setServiceTouchEnd(null);
    setServiceTouchStart(e.touches[0].clientX);
  };
  const onServiceTouchMove = (e) => setServiceTouchEnd(e.touches[0].clientX);
  const onServiceTouchEnd = () => {
    if (!serviceTouchStart || !serviceTouchEnd) return;
    if (serviceTouchStart - serviceTouchEnd > 50) handleServiceNext();
    if (serviceTouchStart - serviceTouchEnd < -50) handleServicePrev();
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title> About Us - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>
      <div className="container-fluid p-0 ">
        {/* Banner Section */}
        <Banner bannerData={aboutUsPageData} />

        {/* About Section - Image and Text */}
        <section>
          <motion.div
            className="py-5 bg-white"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="container">
              <div className="row align-items-center">
                {/* Left Side  Image */}
                <motion.div
                  className="col-lg-6 mb-4 mb-lg-0"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  <div>
                    <img
                      src={AboutImg}
                      alt="Ayyappa Swami Temple"
                      className="img-fluid rounded shadow-lg w-100"
                    />
                  </div>
                </motion.div>

                {/* Right Side Text Content */}
                <motion.div
                  className="col-lg-6 ps-lg-5  px-4 p-md-0"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                >
                  <div>
                    <motion.h2
                      className="fw-bold mb-4 text-center text-md-start heading fs-1"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.6,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.mainHeading}
                    </motion.h2>

                    <motion.p
                      className="mb-4 fs-5 text-muted text-center text-md-start lh-md"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.8,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.description1}
                    </motion.p>

                    <motion.p
                      className=" fs-5 text-muted text-center text-md-start lh-md "
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 1.0,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.description2}
                    </motion.p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </section>

        <section>
          {/* Full Paragraph Section */}
          <motion.div
            className="py-5 bg-light"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    <motion.h3
                      className="fw-bold mb-4 text-center text-md-start heading fs-2"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.4,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.legacyTitle}
                    </motion.h3>

                    <motion.p
                      className="fs-5 text-muted mb-4 text-center text-md-start lh-md px-4 p-md-0"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.6,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.legacyParagraph1}
                    </motion.p>

                    <motion.p
                      className="fs-5 text-muted mb-4 text-center text-md-start lh-md px-4 p-md-0"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.8,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.legacyParagraph2}
                    </motion.p>

                    <motion.p
                      className="fs-5 text-muted text-center text-md-start lh-md px-4 p-md-0"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 1.0,
                        ease: "easeOut",
                      }}
                    >
                      {aboutUsPageData.legacyParagraph3}
                    </motion.p>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ==================== TEMPLE SERVICES (API VERSION) ==================== */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="container-fluid text-center py-5"
              style={{ background: "#fce4e4" }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-12">
                    {/* HEADINGS */}
                    <motion.h4
                      className="text-uppercase fw-bold heading"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      Enhancing Lives with Spiritual Guidance
                    </motion.h4>

                    <motion.h1
                      className="my-3 fw-bold service-title"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      Connecting Devotees Through Spiritual Temple Services
                    </motion.h1>

                    {/* WRAPPER */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="position-relative">
                        {/* DESKTOP LOGIC: Show grid for 3 or fewer cards, carousel for more */}
                        {!isMobile && services.length <= 3 ? (
                          /* ===================== DESKTOP GRID (3 or fewer cards) ===================== */
                          <div className="row justify-content-center align-items-stretch g-4">
                            {services.map((service, index) => (
                              <motion.div
                                key={service.id}
                                className="col-md-4 d-flex"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <ServiceCard
                                  service={service}
                                  onClick={() =>
                                    handleServiceClick(service.slug)
                                  }
                                  className="w-100 h-100"
                                />
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          /* ===================== CAROUSEL (Mobile OR Desktop with more than 3 cards) ===================== */
                          <div className="position-relative">
                            {/* Carousel Container */}
                            <div
                              className="d-flex justify-content-center w-100"
                              ref={servicesRowRef}
                              onTouchStart={onServiceTouchStart}
                              onTouchMove={onServiceTouchMove}
                              onTouchEnd={onServiceTouchEnd}
                              // style={{
                              //   touchAction: "pan-y",
                              //   minHeight: "450px",
                              // }}
                            >
                              <motion.div
                                key={serviceCurrentIndex}
                                className={`d-flex ${
                                  !isMobile
                                    ? "justify-content-center gap-4"
                                    : "justify-content-center"
                                } w-100`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.25 }}
                              >
                                {/* For desktop carousel with more than 3 cards, show 3 cards at once */}
                                {!isMobile && services.length > 3 ? (
                                  <div className="d-flex justify-content-center gap-4 w-100">
                                    {/* Show up to 3 cards in desktop carousel */}
                                    {[0, 1, 2].map((offset) => {
                                      const index =
                                        (serviceCurrentIndex + offset) %
                                        services.length;
                                      const service = services[index];
                                      return (
                                        <div
                                          key={`desktop-${service.id}-${offset}`}
                                          style={{ width: "350px" }}
                                        >
                                          <ServiceCard
                                            service={service}
                                            onClick={() =>
                                              handleServiceClick(service.slug)
                                            }
                                            style={{
                                              width: "100%",
                                              height: "420px",
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  /* Mobile carousel - single card */
                                  <ServiceCard
                                    service={services[serviceCurrentIndex]}
                                    onClick={() =>
                                      handleServiceClick(
                                        services[serviceCurrentIndex].slug
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      maxWidth: "420px",
                                      height: "420px",
                                    }}
                                  />
                                )}
                              </motion.div>
                            </div>

                            {/* Navigation Arrows - Show when needed */}
                            {services.length > 1 && (
                              <motion.div
                                className={`d-flex justify-content-center gap-3 ${
                                  !isMobile && services.length > 3
                                    ? "mt-4"
                                    : "mt-3"
                                }`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                              >
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handleServicePrev}
                                >
                                  <ArrowLeft className="fs-5 heading" />
                                </div>
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handleServiceNext}
                                >
                                  <ArrowRight className="fs-5 heading" />
                                </div>
                              </motion.div>
                            )}
                          </div>
                        )}

                        {/* No Services Available */}
                        {services.length === 0 && (
                          <div className="text-center py-5">
                            <p className="text-muted">
                              Our pooja services are being prepared with
                              devotion. Please stay tuned.
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default AboutUs;
